import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { useData, useUI } from '@/data/stores';
import { Badge, Button, KeyValueGrid, StatusBadge } from '@/components';
import { ConfidenceBar } from '@/components';
import { fmtNumber, fmtRelative, fmtUTC, fmtCoord } from '@/lib/format';
import { severityColor, severityLabel } from '@/lib/color';
import './map.css';

export function ContextPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const selectedAssetId = useUI((s) => s.selectedAssetId);
  const { assets, alerts, edges, otDevices } = useData();

  const asset = assets.find((a) => a.asset_id === selectedAssetId);
  if (!asset) return null;

  const linkedAlerts = alerts
    .filter((a) => a.asset_ids.includes(asset.asset_id))
    .slice(0, 4);
  const downstream = edges
    .filter((e) => e.from_asset_id === asset.asset_id)
    .slice(0, 6);
  const upstream = edges
    .filter((e) => e.to_asset_id === asset.asset_id)
    .slice(0, 6);
  const devices = otDevices.filter((d) => d.asset_id === asset.asset_id);

  return (
    <aside className="iris-context-panel">
      <header className="iris-context-header">
        <div>
          <div className="iris-context-eyebrow">
            <span className="mono">{asset.asset_id}</span>
            <span className="caption">·</span>
            <span className="caption">{asset.country}</span>
          </div>
          <div className="iris-context-title">{asset.name}</div>
        </div>
        <button className="iris-context-close" onClick={onClose} aria-label="Close">
          <X size={16} strokeWidth={1.5} />
        </button>
      </header>

      <section className="iris-context-section">
        <div className="iris-context-row">
          <StatusBadge status={asset.operational_status} />
          <Badge color="var(--text-secondary)">CRIT {asset.criticality_rating}/5</Badge>
          {asset.classification_level !== 'unclassified' && (
            <Badge color="var(--status-info)">
              {asset.classification_level.toUpperCase()}
            </Badge>
          )}
        </div>
      </section>

      <section className="iris-context-section">
        <h3>Overview</h3>
        <KeyValueGrid
          cols={2}
          items={[
            { label: 'Type', value: asset.type.replace(/_/g, ' ') },
            { label: 'Sector', value: asset.sector.replace(/_/g, ' ') },
            { label: 'Operator', value: asset.owner_operator },
            { label: 'Region', value: asset.region ?? '-' },
            {
              label: 'Capacity',
              value: asset.capacity
                ? `${fmtNumber(asset.capacity.value)} ${asset.capacity.unit}`
                : '-',
              mono: true,
            },
            {
              label: 'Voltage',
              value: asset.voltage_level_kv ? `${asset.voltage_level_kv} kV` : '-',
              mono: true,
            },
            { label: 'Coordinates', value: fmtCoord(asset.coordinates.lat, asset.coordinates.lon), mono: true },
            { label: 'Condition', value: `${asset.condition_score}/100`, mono: true },
            {
              label: 'Last telemetry',
              value: asset.last_telemetry_at ? fmtRelative(asset.last_telemetry_at) : '-',
            },
          ]}
        />
      </section>

      {linkedAlerts.length > 0 && (
        <section className="iris-context-section">
          <h3>Active alerts</h3>
          <ul className="iris-context-alert-list">
            {linkedAlerts.map((a) => (
              <li
                key={a.alert_id}
                className="iris-context-alert"
                style={{ borderLeftColor: severityColor(a.severity) }}
                onClick={() => navigate(`/alerts/${a.alert_id}`)}
              >
                <div className="iris-context-alert-row">
                  <span
                    className="iris-context-alert-sev"
                    style={{ color: severityColor(a.severity) }}
                  >
                    ● {severityLabel(a.severity)}
                  </span>
                  <span className="caption">{fmtRelative(a.detected_at)}</span>
                </div>
                <div className="iris-context-alert-title">{a.title}</div>
                <div className="caption iris-context-alert-meta">
                  Confidence <span className="mono">{a.confidence}%</span>
                  {a.protocol && (
                    <>
                      {' · '}
                      <span className="mono">{a.protocol}</span>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(upstream.length + downstream.length) > 0 && (
        <section className="iris-context-section">
          <h3>Dependencies</h3>
          {upstream.length > 0 && (
            <div className="iris-context-deps">
              <div className="caption">Upstream</div>
              {upstream.map((e) => (
                <div key={e.edge_id} className="iris-context-dep-row mono">
                  ← {e.from_asset_id} <span className="caption">{e.relation}</span>
                </div>
              ))}
            </div>
          )}
          {downstream.length > 0 && (
            <div className="iris-context-deps">
              <div className="caption">Downstream</div>
              {downstream.map((e) => (
                <div key={e.edge_id} className="iris-context-dep-row mono">
                  → {e.to_asset_id} <span className="caption">{e.relation}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {devices.length > 0 && (
        <section className="iris-context-section">
          <h3>OT inventory ({devices.length})</h3>
          <ul className="iris-context-devices">
            {devices.slice(0, 6).map((d) => (
              <li key={d.device_id}>
                <span>{d.name}</span>
                <span className="mono caption">
                  {d.vendor} · {d.protocols[0]}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="iris-context-section">
        <h3>Twin metadata</h3>
        <KeyValueGrid
          cols={2}
          items={[
            { label: 'Reg. frameworks', value: asset.regulatory_framework.join(', ') },
            {
              label: 'Data sources',
              value: asset.data_sources.length ? asset.data_sources.join(', ') : '-',
            },
            { label: 'Tags', value: asset.tags.length ? asset.tags.join(' · ') : '-' },
            {
              label: 'Twin confidence',
              value: <ConfidenceBar value={Math.min(100, 60 + asset.condition_score / 4)} />,
            },
            {
              label: 'Last update',
              value: asset.last_telemetry_at ? fmtUTC(asset.last_telemetry_at) : '-',
              mono: true,
            },
          ]}
        />
      </section>

      <footer className="iris-context-footer">
        <Button
          variant="primary"
          iconRight={<ArrowRight size={14} strokeWidth={1.5} />}
          onClick={() => navigate(`/assets/${asset.asset_id}`)}
        >
          Open full record
        </Button>
        <Button
          onClick={() =>
            navigate(`/scenarios?seed=${encodeURIComponent(asset.asset_id)}`)
          }
        >
          Run scenario
        </Button>
      </footer>
    </aside>
  );
}
